import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosClientSignIn } from '../axios/api.js';

// Async thunk for subscribing to push notifications
export const subscribeToPush = createAsyncThunk(
  'push/subscribe',
  async ({ subscription, deviceInfo, userAgent }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.post('/notifications/subscribe', {
        subscription,
        deviceInfo,
        userAgent
      });
      
      if (response.data.success) {
        return { subscription, isSubscribed: true };
      } else {
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Async thunk for unsubscribing from push notifications
export const unsubscribeFromPush = createAsyncThunk(
  'push/unsubscribe',
  async ({ endpoint }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.post('/notifications/unsubscribe', {
        endpoint
      });
      
      if (response.data.success) {
        return { isSubscribed: false };
      } else {
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Async thunk for fetching VAPID public key
export const fetchVapidKey = createAsyncThunk(
  'push/fetchVapidKey',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get('/notifications/vapid-public-key');
      
      if (response.data.success) {
        return response.data.data.publicKey;
      } else {
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  // Subscription state
  isSubscribed: false,
  subscription: null,
  vapidPublicKey: null,
  
  // Browser support
  isSupported: false,
  
  // Loading states
  isLoading: false,
  isSubscribing: false,
  isUnsubscribing: false,
  isFetchingVapid: false,
  
  // Error handling
  error: null,
  
  // Debug info
  debugLogs: [],
};

const pushSlice = createSlice({
  name: 'push',
  initialState,
  reducers: {
    // Set browser support status
    setBrowserSupport: (state, action) => {
      state.isSupported = action.payload;
    },
    
    // Set existing subscription (when checking for existing)
    setExistingSubscription: (state, action) => {
      state.subscription = action.payload.subscription;
      state.isSubscribed = action.payload.isSubscribed;
    },
    
    // Set VAPID public key from environment
    setVapidKeyFromEnv: (state, action) => {
      state.vapidPublicKey = action.payload;
    },
    
    // Clear errors
    clearError: (state) => {
      state.error = null;
    },
    
    // Add debug log
    addDebugLog: (state, action) => {
      const timestamp = new Date().toLocaleTimeString();
      state.debugLogs.unshift(`${timestamp}: ${action.payload}`);
      
      // Keep only last 50 logs
      if (state.debugLogs.length > 50) {
        state.debugLogs = state.debugLogs.slice(0, 50);
      }
    },
    
    // Clear debug logs
    clearDebugLogs: (state) => {
      state.debugLogs = [];
    },
    
    // Reset state (useful for logout)
    resetPushState: (state) => {
      return {
        ...initialState,
        isSupported: state.isSupported, // Keep browser support status
      };
    }
  },
  extraReducers: (builder) => {
    // Fetch VAPID key
    builder
      .addCase(fetchVapidKey.pending, (state) => {
        state.isFetchingVapid = true;
        state.error = null;
      })
      .addCase(fetchVapidKey.fulfilled, (state, action) => {
        state.isFetchingVapid = false;
        state.vapidPublicKey = action.payload;
      })
      .addCase(fetchVapidKey.rejected, (state, action) => {
        state.isFetchingVapid = false;
        state.error = action.payload;
      })
      
      // Subscribe to push
      .addCase(subscribeToPush.pending, (state) => {
        state.isSubscribing = true;
        state.error = null;
      })
      .addCase(subscribeToPush.fulfilled, (state, action) => {
        state.isSubscribing = false;
        state.subscription = action.payload.subscription;
        state.isSubscribed = action.payload.isSubscribed;
      })
      .addCase(subscribeToPush.rejected, (state, action) => {
        state.isSubscribing = false;
        state.error = action.payload;
      })
      
      // Unsubscribe from push
      .addCase(unsubscribeFromPush.pending, (state) => {
        state.isUnsubscribing = true;
        state.error = null;
      })
      .addCase(unsubscribeFromPush.fulfilled, (state, action) => {
        state.isUnsubscribing = false;
        state.subscription = null;
        state.isSubscribed = action.payload.isSubscribed;
      })
      .addCase(unsubscribeFromPush.rejected, (state, action) => {
        state.isUnsubscribing = false;
        state.error = action.payload;
      });
  }
});

export const {
  setBrowserSupport,
  setExistingSubscription,
  setVapidKeyFromEnv,
  clearError,
  addDebugLog,
  clearDebugLogs,
  resetPushState
} = pushSlice.actions;

export default pushSlice.reducer;
